// src/pages/dashboard/castingdirector/CastingDirectorProfilePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import MetaTags from '../../../components/MetaTags';
import { useAuth } from '../../../context/AuthContext';
import api, {fetchCdDirJogetProfileApi} from '../../../services/api';
import { fetchLookupDataApi } from '../../../services/lookupApi';
// import './CastingDirectorProfilePage.css'; // Keep if you have specific page styles

// Components
import CDProfileWizard from '../../../components/dashboard/cdProfile/CDProfileWizard';
import StepCompanyInfo from '../../../components/dashboard/cdProfile/StepCompanyInfo';
import StepAddress from '../../../components/dashboard/cdProfile/StepAddress';
import StepTeamMembers from '../../../components/dashboard/cdProfile/StepTeamMembers';
import StepSocialMedia from '../../../components/dashboard/cdProfile/StepSocialMedia';

// Utils & Schema
import { agencyProfileSchema } from '../../../validation/agencyProfileSchema';
import { CACHE_KEY_AGENCY_LOOKUPS, LOOKUP_MAPPING, WIZARD_STEPS_AGENCY_PROFILE } from '../../../utils/agencyProfileConstants';
import { apiToDateObject, dateObjectToApiFormat, transformToSelectOptions, getNestedError } from '../../../utils/agencyProfileHelpers'; // getNestedError should now come from formUtils
import CryptoJS from "crypto-js";

const CastingDirectorProfilePage = () => {
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [currentJogetRecordId, setCurrentJogetRecordId] = useState(null);
    const [isWhatsappSame, setIsWhatsappSame] = useState(false);

    const [lookupOptions, setLookupOptions] = useState({
        countryOfResidence: [], specialization: [], addressType: [],
        pocDesignation: [], pocContactType: []
    });

    const { control, handleSubmit, reset, trigger, setValue, watch, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(agencyProfileSchema),
        mode: 'onChange',
        defaultValues: { 
            companyIncorporationDate: null, 
            addressType: 'Primary', // Default to 'Primary' as it's a required field in schema
            teamMembers: [{ name: '', email: '', mobile: '', designation: '', contactType: '' }] 
        }
    });

    // --- Pan/Aadhar Conditional Validation Logic ---
    const [firstFilled, setFirstFilled] = useState(null);
    const pan = watch("panNumber");
    const aadhar = watch("aadharNumber");

    useEffect(() => {
        // Clear errors for both if either is filled
        if (pan || aadhar) { clearErrors(["panNumber", "aadharNumber"]); }
    }, [pan, aadhar, clearErrors]);

    useEffect(() => {
        // Trigger validation whenever pan/aadhar changes to re-evaluate conditional rules
        trigger(["panNumber", "aadharNumber"]);
    }, [pan, aadhar, trigger]);

    useEffect(() => {
        // Determine which one was filled first
        if (!firstFilled) {
            if (pan) setFirstFilled("pan");
            else if (aadhar) setFirstFilled("aadhar");
        }
    }, [pan, aadhar, firstFilled]);

    useEffect(() => {
        // Reset firstFilled if both become empty
        if (!pan && !aadhar) { setFirstFilled(null); }
    }, [pan, aadhar]);

    const isPanRequired = (!pan && !aadhar) || firstFilled === "pan";
    const isAadharRequired = (!pan && !aadhar) || firstFilled === "aadhar";
    // --- End Pan/Aadhar Logic ---


    const watchedMobile = watch('mobileNumber');

    // WhatsApp Logic
    useEffect(() => {
        if (isWhatsappSame) setValue('whatsappNo', watchedMobile || '');
    }, [isWhatsappSame, watchedMobile, setValue]);

    const handleWhatsappCheck = (e) => {
        setIsWhatsappSame(e.target.checked);
        if (e.target.checked) setValue('whatsappNo', watchedMobile || '');
        else setValue('whatsappNo', '');
    };

    const SECRET = process.env.REACT_APP_CRYPTO_SECRET;

    const encryptLocal = (data) => {
    return CryptoJS.AES.encrypt(
        JSON.stringify(data),
        SECRET
    ).toString();
    };

    const decryptLocal = (data) => {
    const bytes = CryptoJS.AES.decrypt(data, SECRET);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    };

    // Pincode Logic
    const handlePincodeLookup = useCallback(async (pincode) => {
        if (!pincode || pincode.length !== 6) return;
        try {
            const response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            if (response.data?.[0]?.Status === 'Success') {
                const { State, District } = response.data[0].PostOffice[0];
                setValue('state', State, { shouldValidate: true });
                setValue('district', District, { shouldValidate: true });
            } else setApiMessage({ type: 'warning', text: `Pincode not found.` });
        } catch (error) { console.error("Pincode API Error"); }
    }, [setValue]);
    
    // Load data and lookups
    const loadData = useCallback(async () => {
        if (!user?.id) return;
        setPageLoading(true);
        try {
            // Lookups
            let cachedLookups = localStorage.getItem(CACHE_KEY_AGENCY_LOOKUPS);
            let finalLookups = {};
            if (cachedLookups) {
                // finalLookups = JSON.parse(cachedLookups);
                    finalLookups = decryptLocal(cachedLookups);

            } else {
                const groups = LOOKUP_MAPPING;
                const promises = Object.entries(groups).map(([key, group]) => fetchLookupDataApi(group));
                const results = await Promise.all(promises);
                Object.keys(groups).forEach((key, i) => finalLookups[key] = transformToSelectOptions(results[i].data.data));
                // localStorage.setItem(CACHE_KEY_AGENCY_LOOKUPS, JSON.stringify(finalLookups));
                localStorage.setItem(CACHE_KEY_AGENCY_LOOKUPS, encryptLocal(finalLookups));
                
            }
            setLookupOptions(finalLookups);

            // Profile Data
            const response = await fetchCdDirJogetProfileApi(user.id);
            const jogetData = response.data.jogetData;

            if (jogetData) {
                setCurrentJogetRecordId(jogetData.id || jogetData.pk_id);
                setProfileImagePreview(jogetData.profileImage || null);
                
                const specs = (jogetData.specialization || '').split(',').map(s => s.trim())
                    .map(val => finalLookups.specialization.find(o => o.value === val)).filter(Boolean);

                let teamData = [{ name: '', email: '', mobile: '', designation: '', contactType: '' }];
                if (jogetData.teamMembersJson) {
                    try { teamData = JSON.parse(jogetData.teamMembersJson); } catch (e) {}
                } else if (jogetData.poc_PersonName) { // Fallback for old data structure
                    teamData = [{ 
                        name: jogetData.poc_PersonName || '', 
                        email: jogetData.poc_email || '', 
                        mobile: jogetData.poc_mobileNumber || '', 
                        designation: jogetData.poc_designation || '', 
                        contactType: jogetData.poc_contactType || '' 
                    }];
                }

                reset({
                    ...jogetData,
                    yourName: jogetData.yourName || user.full_name || '',
                    email: jogetData.email || user.email || '',
                    mobileNumber: jogetData.mobileNumber || user.mobile_no || '',
                    alternateMobileNumber: jogetData.alternateMobileNumber || '',
                    specialization: specs,
                    companyIncorporationDate: apiToDateObject(jogetData.companyIncorporationDate),
                    website: jogetData.website || '',
                    addressType: 'Primary', // This might need to come from jogetData if it's dynamic
                    addressLine2: jogetData.addressLine2 || '',
                    whatsappNo: jogetData.whatsappNo || '',
                    gstNumber: jogetData.gstNumber || '',
                    teamMembers: teamData.length > 0 ? teamData : [{ name: '', email: '', mobile: '', designation: '', contactType: '' }] // Ensure at least one team member
                });

                if (jogetData.whatsappNo && jogetData.whatsappNo === jogetData.mobileNumber) setIsWhatsappSame(true);
            } else {
                // Default values for new profile
                reset({ 
                    yourName: user.full_name, 
                    email: user.email, 
                    mobileNumber: user.mobile_no, 
                    addressType: 'Primary', 
                    teamMembers: [{ name: '', email: '', mobile: '', designation: '', contactType: '' }] 
                });
            }
        } catch (error) { 
            console.error("Profile Load Error:", error);
            setApiMessage({ type: 'danger', text: 'Failed to load profile.' }); 
        } finally { setPageLoading(false); }
    }, [user, reset]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleNext = async () => {
        const isValid = await trigger(WIZARD_STEPS_AGENCY_PROFILE[currentStep].fields);
        if (isValid) {
            setApiMessage({ type: '', text: '' });
            if (currentStep < WIZARD_STEPS_AGENCY_PROFILE.length - 1) { setCurrentStep(c => c + 1); window.scrollTo(0,0); }
            else handleSubmit(onSubmit)();
        } else {
            setApiMessage({ type: 'danger', text: 'Please fill all required fields.' });
            window.scrollTo(0,0);
        }
    };

    const handleBack = () => { if (currentStep > 0) setCurrentStep(c => c - 1); window.scrollTo(0,0); };

    // --- UPDATED SUBMIT LOGIC ---
    const onSubmit = async (data) => {
        setLoading(true);
        try {
            // Prepare Data
            const formattedDate = data.companyIncorporationDate ? dateObjectToApiFormat(data.companyIncorporationDate) : '';
            const specializationCSV = (data.specialization || []).map(s => s.value).join(',');
            // const teamJsonString = JSON.stringify(data.teamMembers);
            const teamJsonString = JSON.stringify(
            data.teamMembers.map(m => ({
                ...m,
                designation: m.designation?.value || m.designation,
                contactType: m.contactType?.value || m.contactType
            }))
            );

            // Base Payload
            const basePayload = {
                id: currentJogetRecordId || '',
                yourName: data.yourName,
                email: data.email,
                mobileNumber: data.mobileNumber,
                alternateMobileNumber: data.alternateMobileNumber || '',
                agencyName: data.agencyName,
                website: data.website || '',
                aboutAgency: data.aboutAgency,
                // countryOfResidence: data.countryOfResidence,
                countryOfResidence: data.countryOfResidence?.value || data.countryOfResidence,
                companyIncorporationDate: formattedDate,
                specialization: specializationCSV,
                
                addressType: data.addressType,
                pinCode: data.pinCode,
                addressLine1: data.addressLine1,
                addressLine2: data.addressLine2 || '',
                state: data.state,
                district: data.district,
                panNumber: data.panNumber,
                aadharNumber: data.aadharNumber,
                gstNumber: data.gstNumber || '',

                facebookProfileUrl: data.facebookProfileUrl || '',
                instagramProfileUrl: data.instagramProfileUrl || '',
                linkedinProfileUrl: data.linkedinProfileUrl || '',
                twitterProfileUrl: data.twitterProfileUrl || '',
                youtubeUrl: data.youtubeUrl || '',
                whatsappNo: data.whatsappNo || '',
                imdbUrl: data.imdbUrl || '',

                teamMembersJson: teamJsonString,
                user_id: user.id // Pass user ID explicitly
            };


            if (data.profileImageFile instanceof File) {
                // Multipart Upload
                const formData = new FormData();
                for (const key in basePayload) {
                    formData.append(key, basePayload[key]);
                }
                formData.append('profileImageFile', data.profileImageFile);

                const { data: res } = await api.post('/cddir/profile/cd-dir/withfile', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                handleSuccess(res);

            } else {
                // JSON Upload
                const { data: res } = await api.post('/cddir/profile/cd-dir', basePayload);
                handleSuccess(res);
            }
        } catch (error) {
            console.error("Submit Error:", error);
            setApiMessage({ type: 'danger', text: error.response?.data?.message || 'Failed to save profile.' });
        } finally { setLoading(false); }
    };

    const handleSuccess = (res) => {
        setApiMessage({ type: 'success', text: 'Profile Saved Successfully!' });
        if (res.jogetResponse?.id) setCurrentJogetRecordId(res.jogetResponse.id);
        // Maybe refresh user data if necessary
    };

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setValue('profileImageFile', e.target.files[0]);
            setProfileImagePreview(URL.createObjectURL(e.target.files[0]));
        }
    };
    
    if (pageLoading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

    return (
        <div className="director-profile-page">
            <MetaTags title="Casting Director Profile" />
            <h5 className="text-uppercase text-white fw-bold mb-4"><span className="text-danger border-start border-4 border-danger ps-3">Casting Director Profile</span></h5>

            <CDProfileWizard currentStep={currentStep} />

            {apiMessage.text && <Alert variant={apiMessage.type} dismissible onClose={() => setApiMessage({type:'', text:''})}>{apiMessage.text}</Alert>}

            <div className="custom-card-box">
                <Form noValidate>
                    {currentStep === 0 && (
                        <StepCompanyInfo 
                            control={control} errors={errors} lookupOptions={lookupOptions} 
                            profileImagePreview={profileImagePreview} handleImageChange={handleImageChange} 
                            isWhatsappSame={isWhatsappSame} handleWhatsappCheck={handleWhatsappCheck}
                            isPanRequired={isPanRequired} isAadharRequired={isAadharRequired}
                        />
                    )}

                    {currentStep === 1 && (
                        <StepAddress 
                            control={control} errors={errors} 
                            handlePincodeLookup={handlePincodeLookup} 
                        />
                    )}

                    {currentStep === 2 && (
                        <StepTeamMembers 
                            control={control} errors={errors} lookupOptions={lookupOptions} 
                        />
                    )}

                    {currentStep === 3 && (
                        <StepSocialMedia 
                            control={control} errors={errors} 
                            isWhatsappSame={isWhatsappSame} 
                        />
                    )}
                </Form>
            </div>

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={handleBack} disabled={currentStep === 0 || loading} size="lg" className="px-4 border-secondary text-light"><FaChevronLeft className="me-2" /> Back</Button>
                <Button variant="danger" onClick={handleNext} disabled={loading} size="lg" className="px-5 fw-bold shadow-lg" style={{ minWidth: '180px' }}>{loading ? <Spinner animation="border" size="sm" className="text-white"/> : (currentStep === 3 ? 'SAVE PROFILE' : <>Next <FaChevronRight className="ms-2" /></>)}</Button>
            </div>
        </div>
    );
};
export default CastingDirectorProfilePage;